import { useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { toast } from "sonner";

const fail = (fallback) => (e) => toast.error(e?.response?.data?.detail || fallback);
const plural = (n) => (n > 1 ? "s" : "");

// Actions on an existing RFI package: save / download / send / nudge / log answers.
export function useRfiActions({ active, openPkg, load, onCreated, form, itemsPayload }) {
  const [busy, setBusy] = useState("");
  const [picked, setPicked] = useState({ users: [], contacts: [] });
  const [note, setNote] = useState("");
  const [answering, setAnswering] = useState(null);
  const [answer, setAnswer] = useState({ text: "", by: "", on: "" });

  const run = async (key, fn, fallback) => { setBusy(key); try { await fn(); } catch (e) { fail(fallback)(e); } finally { setBusy(""); } };
  const refresh = async () => { await openPkg(active.id); await load(); onCreated?.(); };

  const save = () => run("save", async () => {
    await api.put(`/rfi-packages/${active.id}`, { to_contact_id: form.to_contact_id || "", response_due: form.response_due, intro: form.intro, items: itemsPayload() });
    toast.success("RFI package saved"); await openPkg(active.id);
  }, "Save failed");
  const download = (fmt) => run(fmt, async () => { await downloadFile(`/rfi-packages/${active.id}?format=${fmt}`, `${active.number}-${active.project.name}.${fmt}`); toast.success(`${fmt.toUpperCase()} downloaded`); }, "Download failed");
  const send = () => {
    if (!picked.users.length && !picked.contacts.length) return toast.error("Choose at least one recipient");
    return run("send", async () => {
      const { data } = await api.post(`/rfi-packages/${active.id}/send`, { recipient_user_ids: picked.users, contact_ids: picked.contacts, note, origin: window.location.origin });
      toast[data.ok ? "success" : "warning"](`${data.sent} of ${data.results.length} email${plural(data.results.length)} sent`);
      setPicked({ users: [], contacts: [] }); setNote(""); await refresh();
    }, "Send failed");
  };
  const nudge = () => run("nudge", async () => { const { data } = await api.post(`/rfi-packages/${active.id}/nudge`, { origin: window.location.origin }); toast.success(`Reminder sent to ${data.to}`); await openPkg(active.id); }, "Reminder failed");
  const flip = (key, id) => setPicked((s) => ({ ...s, [key]: s[key].includes(id) ? s[key].filter((x) => x !== id) : [...s[key], id] }));
  const logAnswer = (fid, clear = false) => {
    if (!clear && !answer.text.trim()) return toast.error("Enter the response text");
    return run("answer", async () => {
      const { data } = await api.put(`/rfi-packages/${active.id}/items/${fid}/response`, clear ? { text: "" } : answer);
      toast.success(clear ? "Answer cleared — finding reopened" : `Answer logged · finding resolved${data.status === "answered" ? " · all items answered" : ""}`);
      setAnswering(null); setAnswer({ text: "", by: "", on: "" }); await refresh();
    }, "Could not log answer");
  };

  return { busy, run, picked, note, setNote, answering, setAnswering, answer, setAnswer, save, download, send, nudge, flip, logAnswer };
}
